import React, { useState } from "react";
import {
  ScrollText, Eye, FileEdit, CheckCircle2, PhoneCall, Plug, Bot, ShieldAlert,
  Search, CalendarDays, ChevronRight,
} from "lucide-react";
import { getAudit } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { PageHeader, LoadingCards, ErrorState, EmptyState } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { fiDateTime } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS = [
  { id: "all", label: "Kaikki" },
  { id: "read", label: "Luku" },
  { id: "draft", label: "Luonnos" },
  { id: "approval", label: "Hyväksyntä" },
  { id: "call_request", label: "Puhelut" },
  { id: "integration", label: "Integraatiot" },
];

const TYPE_ICON = {
  read: Eye, draft: FileEdit, approval: CheckCircle2, update: CheckCircle2,
  create: CheckCircle2, send_email: CheckCircle2, calendar_mutation: CheckCircle2,
  call_request: PhoneCall, integration: Plug, agent: Bot, allowlist: ShieldAlert,
};

export default function Audit() {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");
  const [visibleLimit, setVisibleLimit] = useState(40);
  const { data, loading, error, refetch } = useData(() => getAudit(filter), [filter]);
  const visibleData = (data || []).filter((event) => {
    const matchesQuery = `${event.action} ${event.source} ${event.result}`.toLowerCase().includes(query.toLowerCase());
    if (!matchesQuery || range === "all") return matchesQuery;
    const age = Date.now() - new Date(event.timestamp).getTime();
    return range === "today" ? age <= 86400000 : age <= 604800000;
  });
  const groupedData = Object.values(visibleData.reduce((groups, event) => {
    const timestamp = new Date(event.timestamp);
    const day = Number.isNaN(timestamp.getTime()) ? "unknown" : timestamp.toLocaleDateString("fi-FI");
    const key = `${event.type}|${event.action}|${event.source}|${event.result}|${day}`;
    if (!groups[key]) groups[key] = { ...event, count: 1 };
    else groups[key].count += 1;
    return groups;
  }, {})).sort((left, right) => new Date(right.timestamp) - new Date(left.timestamp));
  const displayedData = groupedData.slice(0, visibleLimit);

  return (
    <div>
      <PageHeader icon={ScrollText} title="Tapahtumaloki" subtitle="Jokainen luku, luonnos, hyväksyntä ja puhelupyyntö." />

      <div className="grid grid-cols-[1fr_145px] gap-2 mb-3">
        <label className="relative">
          <span className="sr-only">Hae tapahtumalokista</span>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="audit-search" value={query} onChange={(event) => { setQuery(event.target.value); setVisibleLimit(40); }} placeholder="Hae tapahtumia…" className="rounded-xl pl-9" />
        </label>
        <Select value={range} onValueChange={(value) => { setRange(value); setVisibleLimit(40); }}>
          <SelectTrigger data-testid="audit-date-filter" className="rounded-xl"><CalendarDays className="h-4 w-4 mr-1 text-primary" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Kaikki ajat</SelectItem><SelectItem value="today">Tänään</SelectItem><SelectItem value="week">7 päivää</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1 mb-4" data-testid="audit-filters">
        {FILTERS.map((f) => (
          <button type="button" key={f.id} data-testid={`audit-filter-${f.id}`} onClick={() => { setFilter(f.id); setVisibleLimit(40); }} aria-pressed={filter === f.id}
            className={cn("whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              filter === f.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground")}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <LoadingCards /> : error ? <ErrorState onRetry={refetch} /> :
        groupedData.length === 0 ? <EmptyState icon={ScrollText} title="Ei tapahtumia" /> : (
          <div className="relative pl-6" data-testid="audit-list">
            <div className="absolute left-2.5 top-2 bottom-2 w-px bg-border" />
            <div className="space-y-4">
              {displayedData.map((ev) => {
                const Icon = TYPE_ICON[ev.type] || ScrollText;
                return (
                  <div key={ev.id} className="relative fade-up" data-testid={`audit-${ev.id}`}>
                    <div className={cn("absolute -left-[18px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-background",
                      ev.result === "Estetty" ? "bg-rose-500/20 text-rose-400" : ev.result === "Luonnos" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400")}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">{ev.action}</p>
                        <StatusBadge status={ev.result} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{ev.source} · {fiDateTime(ev.timestamp)}</span>
                        {ev.count > 1 && <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-foreground">{ev.count} samanlaista</span>}
                      </div>
                      <ChevronRight className="absolute right-3 bottom-3 h-4 w-4 text-muted-foreground/50" />
                    </div>
                  </div>
                );
              })}
            </div>
            {displayedData.length < groupedData.length && (
              <button type="button" onClick={() => setVisibleLimit((limit) => limit + 40)} className="mt-5 w-full rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground hover:border-primary/40">
                Näytä lisää ({groupedData.length - displayedData.length})
              </button>
            )}
          </div>
        )}
    </div>
  );
}
